import { Config } from '@/core/config/config';
import { RemoveMessage } from '@/core/helper/validation-rule-helper';
import { Button as DXButton, DataGrid, FileUploader, LoadPanel, Popup } from 'devextreme-react';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';
import { t } from 'i18next';
import { useCallback, useRef, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom';
import DataGridWrapper from '@/core/wrappers/data-grid-wrapper';
import { productService } from '@/projects/platform/services/product-service';
import { alert } from 'devextreme/ui/dialog';
import { toast } from 'react-toastify';
import { createCustomStore } from '@/core/wrappers/custom-store-wrapper';

const mainStore = createCustomStore({
    cacheRawData: false,
    key: ['id'],
    load: productService.listProduct,
    remove: productService.deleteProduct,
    byKey: (key, loadOptions) => {
        if (key) {
            loadOptions.filter = ["Id", "=", key];
        }
    },
});

const uploadUrl = `${Config.VITE_APP_GATEWAY_URL}/api/product/product/upload-products`

const ProductList = () => {
    const dataGridRef = useRef<DataGrid>(null);
    const navigate = useNavigate();
    const fileUploaderRef = useRef<FileUploader>(null);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [popupVisible, setPopupVisible] = useState(false);
    const [importResult, setImportResult] = useState(null)

    const goToImport = async () => {
        setPopupVisible(true);
    }

    const exportToExcel = async () => {
        const datagrid = dataGridRef.current?.instance;
        if (datagrid?.getSelectedRowKeys() && datagrid?.getSelectedRowKeys().length > 0) {
            onExporting();
            const workbook = new Workbook();
            const worksheet = workbook.addWorksheet('Main Sheet');
            exportDataGrid({
                component: datagrid,
                worksheet: worksheet,
                selectedRowsOnly: true,
                customizeCell: function (options) {
                    options.excelCell.font = { name: 'Calibri', size: 12 };
                    options.excelCell.alignment = { horizontal: 'left' };
                }
            }).then(() => {
                // **AutoFit: Bütün kolonları içeriğe göre genişlet**
                worksheet.columns.forEach((column) => {
                    let maxLength = 0;
                    column.eachCell?.({ includeEmpty: true }, (cell) => {
                        const cellValue = cell.value ? cell.value.toString() : '';
                        maxLength = Math.max(maxLength, cellValue.length);
                    });
                    column.width = maxLength + 10; // +2 padding ekleyerek daha rahat görüntü sağlıyoruz
                });

                const headerStyles = {
                    1: 'FFFFC0CB',
                    2: 'FFFFC0CB',
                    3: 'FFFFC0CB',
                    5: 'FFFFC0CB',
                    6: 'FFFFC0CB',
                    7: 'FFFFA500',
                    8: 'FFFFA500',
                    9: 'FFFFA500',
                    10: 'FFFFA500',
                    11: 'FFFFA500',
                    12: 'FFFFA500',
                    13: 'FFFFA500',
                    14: 'FFFFA500',
                    15: 'FFFFA500',
                    16: 'FFFFA500',
                };

                const headerRow = worksheet.getRow(1);
                Object.entries(headerStyles).forEach(([columnIndex, color]) => {
                    const cell = headerRow.getCell(Number(columnIndex));
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: color }
                    };
                    cell.font = { bold: true, color: { argb: '000000' } }; // Siyah font
                });

                const columnIndex = 5;

                worksheet.getColumn(columnIndex).eachCell((cell, rowNumber) => {
                    if (rowNumber > 1) {
                        cell.value = cell.value == t('active') ? 1 : 0
                    }else{
                        cell.value = t('is_active')
                    }
                });

                return workbook.xlsx.writeBuffer();
            }).then(function () {
                workbook.xlsx.writeBuffer()
                    .then(function (buffer) {
                        saveAs(new Blob([buffer], { type: 'application/octet-stream' }), 'Products.xlsx');
                    });
            }).then(function () {
                onExported();
            })

        }
        else {
            alert(t("please_select_at_least_one_record"), t("warning"))
        }
    }

    const onExporting = function () {
        const datagrid = dataGridRef.current?.instance;
        datagrid?.beginUpdate();
        datagrid?.columnOption("categoryCode1", "visible", true);
        datagrid?.columnOption("categoryName1", "visible", true);
        datagrid?.columnOption("categoryCode2", "visible", true);
        datagrid?.columnOption("categoryName2", "visible", true);
        datagrid?.columnOption("categoryCode3", "visible", true);
        datagrid?.columnOption("categoryName3", "visible", true);
        datagrid?.columnOption("categoryCode4", "visible", true);
        datagrid?.columnOption("categoryName4", "visible", true);
        datagrid?.columnOption("categoryCode5", "visible", true);
        datagrid?.columnOption("categoryName5", "visible", true);
        datagrid?.columnOption("productMeasureUnit", "visible", true);
        datagrid?.columnOption("productBrand", "visible", true);
        datagrid?.columnOption("mainProductCode", "visible", true);
        datagrid?.columnOption("isStockStored", "visible", true);
        datagrid?.columnOption("shelfLifeDays", "visible", true);
    };

    const onExported = function () {
        const datagrid = dataGridRef.current?.instance;
        datagrid?.columnOption("categoryCode1", "visible", false);
        datagrid?.columnOption("categoryName1", "visible", false);
        datagrid?.columnOption("categoryCode2", "visible", false);
        datagrid?.columnOption("categoryName2", "visible", false);
        datagrid?.columnOption("categoryCode3", "visible", false);
        datagrid?.columnOption("categoryName3", "visible", false);
        datagrid?.columnOption("categoryCode4", "visible", false);
        datagrid?.columnOption("categoryName4", "visible", false);
        datagrid?.columnOption("categoryCode5", "visible", false);
        datagrid?.columnOption("categoryName5", "visible", false);
        datagrid?.columnOption("productMeasureUnit", "visible", false);
        datagrid?.columnOption("productBrand", "visible", false);
        datagrid?.columnOption("mainProductCode", "visible", false);
        datagrid?.columnOption("isStockStored", "visible", false);
        datagrid?.columnOption("shelfLifeDays", "visible", false);
        datagrid?.endUpdate();
    };

    const onPopupHiding = useCallback(() => {
        setPopupVisible(false);
    }, [setPopupVisible]);


    const onBeforeSend = async (e: any) => {
        setLoadPanelVisible(true);
        e.request.setRequestHeader("Authorization", 'Bearer ' + localStorage.getItem(Config.VITE_APP_ACCESS_TOKEN_KEY as string))
    }

    const onUploaded = async (e: any) => {
        const response = JSON.parse(e.request.response);
        console.log(response)
        setImportResult(response.data);
        if (response.data.length === 0) {
            toast.success(t("saved_changes"));
            dataGridRef.current?.instance.refresh();
            setPopupVisible(false);
        }
        setLoadPanelVisible(false);
    }

    const onUploadError = async (e: any) => {
        toast.error(e.message);
        setLoadPanelVisible(false);
    }
    const position = { of: '#panel' };

    const exportToExcelBtnOptions = useMemo(() => ({
        text: t('export'),
        type: 'normal',
        icon: 'export',
        onClick: exportToExcel
    }), []);

    const importToExcelBtnOptions = useMemo(() => ({
        text: t('import'),
        type: 'normal',
        icon: 'import',
        onClick: goToImport
    }), []);




    return (
        <>
            <DataGridWrapper
                props={{
                    id: 'product-list', ref: dataGridRef, wordWrapEnabled: false,
                }}
                permissonCode='Product.Product'
                datasource={mainStore}
                filterVisible={true}
                headerFilterVisible={true}
                searchPanelVisible={true}
                selectionConfig={{ mode: 'multiple', selectAllMode: 'allPages', showCheckBoxesMode: 'always' }}
                editingConfig={{ allowAdding: false, allowDeleting: true, allowUpdating: true, confirmDelete: true, confirmDeleteMessage: RemoveMessage('Product') }}
                pagerVisible={true}
                isAddNewBtn={true}
                navigateNewRecordPath='/product-definition'
                enableCustomToolbarContent={true}
                columns={[
                    {
                        dataField: 'productCode', caption: t('product_code')
                    },
                    { dataField: 'name', caption: t('product_name') },
                    { dataField: 'barcodes', caption: t('barcode'), allowSorting: false, allowSearch: false, allowFiltering: false },
                    { dataField: 'barcodeOfBox', caption: t('barcode_of_box'), visible: true },
                    {
                        dataField: 'isActive', caption: t('status'),
                        calculateCellValue: (rowData: any) => {
                            return rowData.isActive ? t("active") : t("passive");
                        },
                    },
                    { dataField: 'productMeasureUnit', caption: t('measure_unit'), visible: false },
                    { dataField: 'productGroup', caption: t('product_group'), allowExporting: true, visible: false },
                    { dataField: 'categoryCode1', caption: t('category_code_1'), visible: false },
                    { dataField: 'categoryName1', caption: t('category_name_1'), visible: false },
                    { dataField: 'categoryCode2', caption: t('category_code_2'), visible: false },
                    { dataField: 'categoryName2', caption: t('category_name_2'), visible: false },
                    { dataField: 'categoryCode3', caption: t('category_code_3'), visible: false },
                    { dataField: 'categoryName3', caption: t('category_name_3'), visible: false },
                    { dataField: 'categoryCode4', caption: t('category_code_4'), visible: false },
                    { dataField: 'categoryName4', caption: t('category_name_4'), visible: false },
                    { dataField: 'categoryCode5', caption: t('category_code_5'), visible: false },
                    { dataField: 'categoryName5', caption: t('category_name_5'), visible: false },
                    { dataField: 'productBrand', caption: t('brand'), visible: false },
                    { dataField: 'mainProductCode', caption: t('main_product_code'), visible: false },
                    { dataField: 'isStockStored', caption: t('is_stock_stored'), visible: false },
                    { dataField: 'shelfLifeDays', caption: t('shelf_life_days'), visible: false },

                    {
                        dataField: 'approvalStatus', caption: t('approval_status'), allowExporting: false
                    },
                    {
                        dataField: 'isTemp', caption: "", sortOrder: "desc",
                        sortIndex: 0, visible: false
                    }
                ]}
                buttonConfig={[
                    {
                        name: 'edit',
                        icon: 'edit',
                        onClick: function (e: any) {
                            navigate(`/product-definition/${e.row.data.requestId ?? e.row.data.id}`, { state: { id: e.row.data.requestId ?? e.row.data.id } })
                        },
                    },
                    {
                        name: 'delete',
                        icon: 'trash',
                    },
                ]}

                customToolbarItems={[
                    {
                        widget: "dxButton",
                        location: "after",
                        options: exportToExcelBtnOptions
                    },
                    {
                        widget: "dxButton",
                        location: "after",
                        options: importToExcelBtnOptions
                    }
                ]}
            />


            <Popup
                visible={popupVisible}
                onHiding={onPopupHiding}
                showTitle={true}
                width={900}
                height={520}
                position={{
                    of: window,
                    at: 'center',
                    my: 'center'
                }}
                title={t("import_products")}
            >

                <div id="panel" style={{ height: 500 }}>
                    {/* <SingleCard title={t("import_products")}> */}

                    <div className="row justify-content-center">
                        <div className='col-auto mb-2'>

                            <FileUploader
                                ref={fileUploaderRef}
                                width={150}
                                selectButtonText={t("select_file")}
                                uploadButtonText={t("upload")}
                                showFileList={false}

                                labelText=""
                                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                uploadMode="instantly"
                                uploadUrl={uploadUrl}
                                onBeforeSend={onBeforeSend}
                                onUploaded={onUploaded}
                                onUploadError={onUploadError}
                            //disabled={allDisabled}
                            />
                            <a
                                href="#"
                                onClick={async (e) => {
                                    e.preventDefault(); // Tarayıcı varsayılan davranışını engelle
                                    await productService.downloadExcel();
                                }}
                            >
                                <i style={{ fontSize: "14px" }} className="dx-icon dx-icon-download"></i>
                                Excel Template
                            </a>
                        </div>

                    </div >


                    {<DataGridWrapper
                        props={{ wordWrapEnabled: true, paging: { visible: false, enabled: false }, columnWidth: 150 }}
                        datasource={importResult}
                        remoteOperations={false}
                        height={300}
                        pagerVisible={false}
                        headerFilterVisible={true}
                        filterVisible={false}
                        editingConfig={{}}
                        enableCustomToolbarContent={false}
                        columnChooser={true}
                        searchPanelVisible={false}
                        columns={[

                            {
                                dataField: 'results',
                                caption: t("errors"),
                                allowFiltering: true,
                                width: 300
                            },
                            {
                                dataField: 'productCode',
                                caption: t("product_code"),
                                allowFiltering: true,
                            },
                            {
                                dataField: 'name',
                                caption: t("product_name"),
                                allowFiltering: true,
                            },
                            {
                                dataField: 'barcode',
                                caption: t("barcode"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'barcodeOfBox',
                                caption: t("barcode_of_box"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'isActive',
                                caption: t("status"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'measureUnit',
                                caption: t("measure_unit"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'categoryCode1',
                                caption: t("product_group_code1"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'category1',
                                caption: t("product_group_name1"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'categoryCode2',
                                caption: t("product_group_code2"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'category2',
                                caption: t("product_group_name2"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'categoryCode3',
                                caption: t("product_group_code3"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'category3',
                                caption: t("product_group_name3"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'categoryCode4',
                                caption: t("product_group_code4"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'category4',
                                caption: t("product_group_name4"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'categoryCode5',
                                caption: t("product_group_code5"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'category5',
                                caption: t("product_group_name5"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'brand',
                                caption: t("brand"),
                                allowFiltering: true,
                                visible:false
                            },
                            {
                                dataField: 'mainProduct',
                                caption: t("main_product"),
                                allowFiltering: true,
                                visible:false
                            }
                        ]}
                    />}
                </div >
                <LoadPanel
                    shadingColor="rgba(0,0,0,0.4)"
                    position={position}
                    visible={loadPanelVisible}
                    showIndicator={true}
                    shading={false}
                    showPane={true}
                    activeStateEnabled={false}
                />

            </Popup>

        </>
    )
}
export default ProductList